import { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Smartphone, Download, X } from 'lucide-react';

function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    // Check if already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }
    
    // Check if dismissed before
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }
    
    // Detect mobile device
    const userAgent = navigator.userAgent.toLowerCase();
    const isAndroidDevice = /android/i.test(userAgent);
    const isMobileDevice = /mobile|tablet|android|iphone|ipad/i.test(userAgent);
    const isSmallScreen = window.innerWidth <= 768;
    
    setIsMobile(isMobileDevice || isSmallScreen);
    
    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/admin-sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
        })
        .catch((error) => {
          console.log('Service Worker registration failed:', error);
        });
    }
    
    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show custom prompt for mobile users
      if (isMobileDevice || isSmallScreen) {
        setTimeout(() => {
          setShowPrompt(true);
        }, 2000);
      }
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
      console.log('PWA was installed');
    });
    
    // For mobile devices, show prompt after delay even without beforeinstallprompt
    // This handles browsers that don't fire the event
    if ((isMobileDevice || isSmallScreen) && (isAndroidDevice || /iphone|ipad/i.test(userAgent))) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    }
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);
  
  const handleInstall = async () => {
    if (deferredPrompt) {
      // Use the native prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('User response:', outcome);
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    } else {
      // Show instructions for manual installation
      const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
      if (isIOS) {
        alert('To install on iOS:\n1. Tap the Share button at the bottom\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" to confirm');
      } else {
        alert('To install:\n1. Tap the menu button (⋮) in your browser\n2. Select "Add to Home Screen" or "Install App"\n3. Tap "Install" to confirm');
      }
    }
    setShowPrompt(false);
  };
  
  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };
  
  if (isInstalled || !showPrompt) {
    return null;
  }
  
  return (
    <AlertDialog open={showPrompt} onOpenChange={setShowPrompt}>
      <AlertDialogContent className="max-w-md mx-4">
        <AlertDialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="p-4 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-lg">
              <Smartphone className="w-12 h-12 text-white" />
            </div>
          </div>
          <AlertDialogTitle className="text-center text-xl font-bold">
            Install Trackly App
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-center text-gray-600">
              <p>Get the best experience by installing our app on your device!</p>
              <div className="mt-4 p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl text-sm">
                <p className="font-semibold text-purple-800 mb-3">Why install?</p>
                <ul className="text-left space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    Quick access from home screen
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    Full screen experience
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    Works like a native app
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    Faster loading times
                  </li>
                </ul>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
          <AlertDialogAction
            onClick={handleInstall}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold py-3"
            data-testid="pwa-install-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            Yes, Install App
          </AlertDialogAction>
          <AlertDialogCancel 
            onClick={handleDismiss}
            className="w-full border-gray-300"
            data-testid="pwa-dismiss-btn"
          >
            <X className="w-4 h-4 mr-2" />
            Maybe Later
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default PWAInstallPrompt;
